import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterOutlet, RouterLink } from '@angular/router';
import { environment } from '../environments/environment';
import { LucideAngularModule } from 'lucide-angular';
import { CookieService } from "ngx-cookie-service";
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { QuizNavComponent } from './features/quiz/components/quiz-nav/quiz-nav.component';
import { LoaderService } from './core/services/loader.service';
import { getStoredAuthToken, setSessionAuthToken, clearSessionAuthToken } from './core/auth/auth-token';
import { MAINTENANCE_MODE } from './maintenance-mode';
import { OfflinePageComponent } from './features/offline/offline-page.component';
import { ToastComponent } from './layout/toast/toast.component';
import { ToastService } from './layout/toast/toast.service';
import { Chatting } from './features/home/components/chatting/chatting.component';
import { SiteSettingsService } from './core/services/site-settings.service';
import { UserProfileService } from './core/services/users/userDetails.service';
import { MaintenancePageComponent } from './maintenance-page.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, LucideAngularModule, QuizNavComponent, OfflinePageComponent, ToastComponent, Chatting, MaintenancePageComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  authToken: string = '';
  showQuizNav = false;
  isOnline = true;
  isChatWidgetOpen = false;
  isMaintenanceMode = false;
  isAdminRoute = false;
  isMaintenanceRoute = false;
  isAdminUser = false;
  allowAdminDuringMaintenance = true;
  private readonly http = inject(HttpClient, { optional: true });
  private readonly profileService = inject(UserProfileService);

  get showMaintenanceOnRoot(): boolean {
    if (this.isMaintenanceMode && this.allowAdminDuringMaintenance && this.isAdminUser) {
      return false;
    }
    return this.isMaintenanceMode && !this.isAdminRoute && !this.isMaintenanceRoute;
  }
  private destroy$ = new Subject<void>();
  private onlineHandler = () => { this.isOnline = true; };
  private offlineHandler = () => { this.isOnline = false; };

  toggleChatWidget(): void {
    this.isChatWidgetOpen = !this.isChatWidgetOpen;
  }

  closeChatWidget(): void {
    this.isChatWidgetOpen = false;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieService: CookieService, 
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private toast: ToastService,
    private siteSettingsService: SiteSettingsService
  ) { }

  ngOnInit() {
    this.siteSettingsService.maintenanceStatusStream$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (status) => {
        this.isMaintenanceMode = status.maintenance_mode === true;
        this.allowAdminDuringMaintenance = status.maintenance_allow_admin !== false;
        if (this.isMaintenanceMode && !(this.allowAdminDuringMaintenance && this.isAdminUser)) {
          this.isChatWidgetOpen = false;
        }
      },
      error: () => {}
    });
    this.siteSettingsService.getMaintenanceStatus().subscribe({ error: () => {} });

    if (isPlatformBrowser(this.platformId)) {
      this.profileService.isAdmin().pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
        this.isAdminUser = isAdmin;
      });
      this.isOnline = navigator.onLine;
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);
    }

    // Global loader disabled as of now
    // if (isPlatformBrowser(this.platformId)) {
    //   if (this.router.navigated) {
    //     this.loaderService.hideManual();
    //   } else {
    //     this.loaderService.showManual({ message: 'Rolling the Reel' });
    //     this.router.events
    //       .pipe(
    //         filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    //         take(1),
    //         takeUntil(this.destroy$)
    //       )
    //       .subscribe(() => {
    //         this.loaderService.hideManual();
    //       });
    //     setTimeout(() => {
    //       this.loaderService.hideManual();
    //     }, 1000);
    //   }
    // } else {
    //   this.loaderService.hideManual();
    // }

    if (isPlatformBrowser(this.platformId)) {
      this.authToken = getStoredAuthToken();

      this.route.queryParams
        .pipe(takeUntil(this.destroy$))
        .subscribe(params => {
          this.generateSession(params);
        });
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.syncQuizTheme(event.urlAfterRedirects);
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          }, 0);

          // Track page view analytics for consumer pages (fire-and-forget)
          const cleanUrl = (event.urlAfterRedirects || '').split('?')[0];
          if (this.http && !this.isAdminRoute && !this.isMaintenanceRoute && cleanUrl) {
            let cat = 'page';
            if (cleanUrl.startsWith('/movie')) cat = 'movie';
            else if (cleanUrl.startsWith('/tv')) cat = 'tv';
            else if (cleanUrl.startsWith('/quiz')) cat = 'quiz';
            else if (cleanUrl.startsWith('/blog')) cat = 'blog';
            else if (cleanUrl === '/' || cleanUrl === '') cat = 'home';

            const api = environment.backendUrl || '';
            this.http.post(`${api}/api/admin/dashboard/page-view`, {
              path: cleanUrl,
              title: typeof document !== 'undefined' ? (document.title || cleanUrl) : cleanUrl,
              category: cat
            }).subscribe({ error: () => {} });
          }
        }
      });

    this.syncQuizTheme(this.router.url);

    // Show pending toast from logout (stored before redirect)
    if (isPlatformBrowser(this.platformId)) {
      const pendingToast = sessionStorage.getItem('pending_toast');
      if (pendingToast) {
        sessionStorage.removeItem('pending_toast');
        try {
          const t = JSON.parse(pendingToast);
          this.toast.show(t.type, t.title, t.message, 4000);
        } catch (_) {}
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loaderService.hideManual();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('online', this.onlineHandler);
      window.removeEventListener('offline', this.offlineHandler);
    }
  }

  generateSession(params: any) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const sessionData = params['session_details'];
    const directToken = params['session_token'] || params['token'];
    const loginSuccess = params['login_success'];

    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        const token = typeof parsed?.token === 'string' ? parsed.token.trim() : '';
        if (token) {
          const isSecure = location.protocol === 'https:';
          this.cookieService.set('auth_token', token, {
            expires: 7,
            path: '/',
            secure: isSecure,
            sameSite: 'Lax'
          });
          setSessionAuthToken(token);
          this.authToken = token;
        }

        try {
          const userName = parsed?.user_info?.name || parsed?.user_info?.display_name || '';
          const firstName = userName.split(' ')[0] || 'there';
          this.toast.success('Welcome back!', `Hi ${firstName}, glad to see you again.`);
        } catch (_) {
          this.toast.success('Welcome back!', 'You are now signed in.');
        }
        
        this.router.navigate(['/'], {
          queryParams: {},
          replaceUrl: true,
        });
        return;
      } catch (error) {
        console.error('Parse error:', error);
      }
    }

    if (directToken) {
      const token = String(directToken).trim();
      if (token) {
        const isSecure = location.protocol === 'https:';
        this.cookieService.set('auth_token', token, {
          expires: 7,
          path: '/',
          secure: isSecure,
          sameSite: 'Lax'
        });
        setSessionAuthToken(token);
        this.authToken = token;
        this.toast.success('Welcome back!', 'You are now signed in.');
      }
      this.router.navigate(['/'], {
        queryParams: {},
        replaceUrl: true,
      });
      return;
    }

    if (loginSuccess) {
      const storedToken = getStoredAuthToken();
      if (storedToken) {
        this.authToken = storedToken;
      }
      this.toast.success('Welcome back!', 'You are now signed in.');
      this.router.navigate(['/'], {
        queryParams: {},
        replaceUrl: true,
      });
      return;
    }

    const fallbackToken = getStoredAuthToken();
    if (fallbackToken) {
      this.authToken = fallbackToken;
    }
  }

  private syncQuizTheme(url: string): void {
    const cleanUrl = (url || '').split('?')[0];
    this.showQuizNav = cleanUrl === '/quiz' || cleanUrl.startsWith('/quiz/');
    this.isAdminRoute = cleanUrl.startsWith('/admin') || cleanUrl.startsWith('/auth');
    this.isMaintenanceRoute = cleanUrl === '/maintenance' || cleanUrl.startsWith('/maintenance/');
    if (this.isMaintenanceRoute || this.isMaintenanceMode) {
      this.isChatWidgetOpen = false;
    }
    this.checkMaintenanceRedirect(url);
  }

  private checkMaintenanceRedirect(url: string): void {
    // In-place maintenance component handles presentation without breaking SEO via redirects
  }
}
